import { makeAutoObservable } from "mobx";
import { firstValueFrom } from "rxjs";
import TimeZoneStore from "./TimeZoneStore";
import CountryService from "../services/CountryService";
import AccountService from "../services/AccountService";
import { GeneralSettingsModel } from "../models/GeneralSettingsModel";
import { CountryWithStateModel } from "../models/CountryModels";
import { TimeZoneModel } from "../models/TimeZoneModel";
import { AuthService, UtilityMethods } from "@mindmemobile/mm-shared-global";
import { BeneficiaryService } from "../services";

export class AccountSettingsStore {
  public generalSettings: GeneralSettingsModel = new GeneralSettingsModel();
  public isLoadingInProgress = true;
  public beneficiaryService: BeneficiaryService = new BeneficiaryService();
  private loadedAccountId: string | null = null;

  constructor() {
    makeAutoObservable(this);
    this.loadGeneralSettings();
  }

  public setGeneralSettings = (generalSettings: GeneralSettingsModel) => {
    this.generalSettings = generalSettings;
  };

  public loadGeneralSettings = async (accountId?: string) => {
    const effectiveAccountId = accountId || AuthService.authData.accountId;

    // If settings are already loaded for the same account, skip
    if (
      this.generalSettings.countries.length > 0 &&
      this.loadedAccountId === effectiveAccountId
    ) {
      this.isLoadingInProgress = false;
      return;
    }

    this.isLoadingInProgress = true;
    const countryResponse = await firstValueFrom(
      CountryService.getCountriesWithState(),
    );
    const countries = countryResponse?.result.map(
      (country) => new CountryWithStateModel(country),
    );

    // const countries = countryStore.countriesWithState;
    if (!effectiveAccountId && !AuthService.hasAccount) {
      return;
    }
    if (
      this.beneficiaryService.beneficiaries.length === 0 &&
      this.beneficiaryService.isLoadingInProgress
    ) {
      this.beneficiaryService.loadAllBeneficiaries(effectiveAccountId);
    }
    const settingsResponse = await firstValueFrom(
      AccountService.getGeneralSettings(effectiveAccountId),
    );
    if (settingsResponse?.result) {
      const { timeZoneId, countryId } = settingsResponse.result;
      const timeZone =
        TimeZoneStore.timeZones.find((tz) => tz.id === timeZoneId) ||
        new TimeZoneModel();
      const country = countries?.find((c) => c.id === countryId);

      const deserializedData = GeneralSettingsModel.deserialize(
        settingsResponse.result,
      );
      this.setGeneralSettings(
        new GeneralSettingsModel({
          ...deserializedData,
          timeZone,
          country: country || new CountryWithStateModel(),
          countries: countries || [],
        }),
      );
      this.loadedAccountId = effectiveAccountId;
    }
    this.isLoadingInProgress = false;
  };

  public sanitizeAmount(value: string): string {
    switch (this.generalSettings.numberFormat) {
      case "Format2": // "1.234.567,89" → dots are thousands, comma is decimal
        value = value.replace(/\./g, "").replace(",", ".");
        break;
      case "Format3": // "1 234 567,89" → spaces are thousands, comma is decimal
        value = value.replace(/ /g, "").replace(",", ".");
        break;
      case "Format4": // "1'234'567.89" → apostrophes are thousands
        value = value.replace(/'/g, "");
        break;
      case "Format1": // "1,234,567.89" → commas are thousands
      default:
        value = value.replace(/,/g, "");
        break;
    }
    return value;
  }

  public formatAmount(x: number, needToFixed = true): string {
    const value = needToFixed ? UtilityMethods.fixNumber(x) : x.toString();

    switch (this.generalSettings.numberFormat) {
      case "Format1":
        return value.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      case "Format2": // "1.234.567,89"
        return value.replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
      case "Format3": // "1 234 567,89"
        return value.replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, " ");
      case "Format4": // "1'234'567.89"
        return value.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
      case "Format5": // "1234567.89"
        return value.replace(",", "").replace(/ /g, "");
      default:
        return value;
    }
  }

  public formatAmountWithoutCommas(x: number, needToFixed = true): string {
    return needToFixed ? UtilityMethods.fixNumber(x) : x.toString();
  }

  public formatAmountText(x: number, needToFixed = true): string {
    const number = x < 0 ? Math.abs(x) : x;
    const amount = this.formatAmount(number, needToFixed);
    return x < 0 ? `($${amount})` : `$${amount}`;
  }
}

export default new AccountSettingsStore();
